import { useEffect, useRef, useState } from 'react';
import { Check, Search, Users } from 'lucide-react';

const STAFF_ROLES = ['technician', 'admin', 'owner'];

interface Props {
  /** Full company member list — options show staff only, but selected names
   *  resolve against the whole list so an existing assignee never renders
   *  as a raw user id. */
  members: any[];
  /** Selected user ids, primary first. */
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  /** User ids covered by the assigned team — tagged "team" in the list. */
  teamMemberIds?: Set<string>;
  placeholder?: string;
}

/* Multi-assignee picker: first selected = primary. Everything works by
   tap alone (no ctrl/cmd-click, no hover) so it's usable on touchscreens:
   tap rows to toggle, tap a chip name to make it primary, tap × to remove. */
export default function AssigneePicker({
  members,
  selectedIds,
  onChange,
  teamMemberIds,
  placeholder = 'Unassigned',
}: Props) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const nameOf = (id: string) => {
    const m = members.find((m: any) => m.user?.id === id);
    return m?.user?.name ?? m?.user?.email ?? 'Former member';
  };

  const staff = members.filter((m: any) => STAFF_ROLES.includes(m.role));
  const filtered = staff.filter((m: any) => {
    const name = m.user?.name ?? m.user?.email ?? '';
    return name.toLowerCase().includes(search.toLowerCase());
  });

  const toggle = (userId: string) => {
    if (selectedIds.includes(userId)) {
      onChange(selectedIds.filter(id => id !== userId));
    } else {
      onChange([...selectedIds, userId]);
    }
  };

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="input w-full text-left flex items-center justify-between gap-2"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className={`truncate ${selectedIds.length === 0 ? 'text-muted' : ''}`}>
          {selectedIds.length === 0 ? placeholder : selectedIds.map(nameOf).join(', ')}
        </span>
        <Users className="w-4 h-4 text-muted shrink-0" />
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full bg-surface border border-line rounded-lg shadow-xl overflow-hidden">
          <div className="p-2 border-b border-line-light">
            <div className="flex items-center gap-2 px-2 py-1 bg-row-odd rounded-md">
              <Search className="w-3.5 h-3.5 text-muted" />
              <input
                className="flex-1 text-sm bg-transparent outline-none text-fg placeholder:text-muted"
                placeholder="Search staff…"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
          </div>
          <div className="max-h-48 overflow-y-auto py-1" role="listbox" aria-multiselectable="true">
            {filtered.map((m: any) => {
              const uid = m.user?.id;
              const name = m.user?.name ?? m.user?.email;
              const selected = selectedIds.includes(uid);
              const inTeam = teamMemberIds?.has(uid);
              return (
                <button
                  key={uid}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  onClick={() => toggle(uid)}
                  className="w-full px-3 py-2 text-sm text-left hover:bg-row-odd flex items-center gap-2"
                >
                  <span className="w-5 flex items-center justify-center shrink-0">
                    {selected && <Check className="w-3.5 h-3.5 text-primary" />}
                  </span>
                  <span className="w-7 h-7 rounded-full brand-gradient flex items-center justify-center text-white text-xs font-semibold shrink-0">
                    {(name ?? '?')[0].toUpperCase()}
                  </span>
                  <span className="text-fg truncate">{name}</span>
                  <span className="ml-auto text-xs text-muted capitalize shrink-0">
                    {inTeam ? 'team · ' : ''}{m.role}
                  </span>
                </button>
              );
            })}
            {filtered.length === 0 && (
              <p className="px-3 py-2 text-sm text-muted">No staff found</p>
            )}
          </div>
        </div>
      )}

      {selectedIds.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {selectedIds.map((id, idx) => (
            <span
              key={id}
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                idx === 0 ? 'bg-primary-muted text-primary' : 'bg-row-odd text-muted'
              }`}
            >
              {idx === 0 && <span className="opacity-60">primary</span>}
              {idx === 0 ? (
                <span>{nameOf(id)}</span>
              ) : (
                <button
                  type="button"
                  title="Make primary"
                  onClick={() => onChange([id, ...selectedIds.filter(i => i !== id)])}
                  className="hover:underline"
                >
                  {nameOf(id)}
                </button>
              )}
              <button
                type="button"
                onClick={() => onChange(selectedIds.filter(i => i !== id))}
                className="ml-0.5 opacity-60 hover:opacity-100"
                aria-label={`Remove ${nameOf(id)}`}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
      {selectedIds.length > 1 && (
        <p className="text-[11px] text-muted mt-1">Tap a name to make it the primary assignee.</p>
      )}
    </div>
  );
}
